import { Button } from '@ps-design-system/components/Button';
import { PriceSummaryRow } from '@ps-design-system/components/PriceSummary';
import { Text } from '@ps-design-system/components/Text';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger
} from '@ps-design-system/components/Tooltip';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PricingRulesDialog } from '../PricingRulesSummary/PricingRulesDialog';

export const createHourlyRateLabel = (booking, currency) => {
  const formattedRate = (
    <FormattedNumber
      value={booking?.rate || 0}
      style="currency"
      currency={currency}
    />
  );

  const duration = booking?.duration || 0;
  const formattedDuration = (
    <>
      {duration}{' '}
      <FormattedMessage
        id="price_summary.hourly_rate_label"
        defaultMessage="{duration, plural, one {hour} other {hours}}"
        values={{ duration }}
      />
    </>
  );

  return (
    <>
      {formattedRate} x {formattedDuration}
    </>
  );
};

export const HourlyRateLineItem = ({
  booking,
  currency,
  accessToken,
  listingId,
  tooltipAlign = 'center'
}) => {
  const [pricingRulesDialogOpen, setPricingRulesDialogOpen] =
    React.useState(false);

  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <>
      <PriceSummaryRow
        testId="hourly-rate-line-item"
        name={(
          <Tooltip
            rootProps={{
              delayDuration: 0,
              open: showTooltip,
              onOpenChange: setShowTooltip
            }}
          >
            <TooltipTrigger
              onClick={(e) => {
                e.preventDefault();
                setShowTooltip((prev) => !prev);
              }}
            >
              <Text style={{ textDecoration: 'underline' }}>
                {createHourlyRateLabel(booking, currency)}
              </Text>
            </TooltipTrigger>
            <TooltipContent
              style={{ maxWidth: '248px' }}
              align={tooltipAlign}
            >
              <Text typeface="body-1-long">
                <FormattedMessage
                  id="price_summary.hourly_rate_tooltip"
                  defaultMessage="This is a combined price based on the listing's hourly rates and attendee pricing. {pricingRulesBtn}"
                  values={{
                    pricingRulesBtn: (
                      <Button
                        variant="text-only"
                        className="tw-body-1-underline"
                        onClick={() => setPricingRulesDialogOpen(true)}
                      >
                        <FormattedMessage
                          id="price_summary.pricing_rules"
                          defaultMessage="View pricing rules"
                        />
                      </Button>
                    )
                  }}
                />
              </Text>
              <TooltipArrow className="tw-fill-base-white" />
            </TooltipContent>
          </Tooltip>
        )}
        value={(
          <FormattedNumber
            value={booking?.subtotal_amount || 0}
            style="currency"
            currency={currency}
          />
        )}
      />

      <PricingRulesDialog
        isOpen={pricingRulesDialogOpen}
        setIsOpen={setPricingRulesDialogOpen}
        pricingRules={booking?.rate_details}
        currency={currency}
        accessToken={accessToken}
        listingId={listingId}
      />
    </>
  );
};

HourlyRateLineItem.displayName = 'HourlyRateLineItem';
HourlyRateLineItem.propTypes = {
  booking: PropTypes.object,
  currency: PropTypes.string,
  accessToken: PropTypes.string,
  listingId: PropTypes.string,
  tooltipAlign: PropTypes.string
};
